import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Sparkles, Loader2, FileBarChart, CheckCircle2, Download } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/apiClient";
import { generateReportPdf } from "@/lib/reportPdf";
import { EmptyState } from "@/components/dashboard/primitives";
import { Button } from "@/components/ui/button";

function Section({ title, children }) {
  return (
    <section>
      <p className="text-xs uppercase tracking-[0.16em] text-accent font-bold mb-2">{title}</p>
      {children}
    </section>
  );
}

function List({ items }) {
  return (
    <ul className="space-y-1.5 text-sm text-muted-foreground">
      {(items || []).map((t, i) => <li key={i} className="flex items-start gap-2"><span className="mt-1.5 h-1 w-1 rounded-full bg-accent shrink-0" />{t}</li>)}
    </ul>
  );
}

function ReportView({ content }) {
  return (
    <div className="rounded-xl border border-border bg-card p-6 space-y-6" data-testid="report-view">
      <Section title="Executive Summary"><p className="text-sm text-muted-foreground leading-relaxed">{content.executive_summary}</p></Section>
      <div className="grid sm:grid-cols-2 gap-6">
        <Section title="Key Themes"><List items={content.key_themes} /></Section>
        <Section title="Top Concerns"><List items={content.top_concerns} /></Section>
      </div>
      <Section title="Audience Questions"><List items={content.audience_questions} /></Section>
      <Section title="Session Feedback"><List items={content.session_feedback} /></Section>
      <div className="grid sm:grid-cols-2 gap-6">
        <Section title="Recommendations"><List items={content.recommendations} /></Section>
        <Section title="Action Items">
          <ul className="space-y-2 text-sm text-muted-foreground">
            {(content.action_items || []).map((t, i) => <li key={i} className="flex items-start gap-2"><CheckCircle2 className="h-4 w-4 text-accent mt-0.5 shrink-0" />{t}</li>)}
          </ul>
        </Section>
      </div>
    </div>
  );
}

export default function EventReports() {
  const { event } = useOutletContext();
  const [reports, setReports] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [active, setActive] = useState(null);

  const load = () => api.get(`/events/${event.id}/reports`).then(({ data }) => {
    setReports(data);
    if (data.length && !active) setActive(data[0]);
  });
  useEffect(() => { load(); }, [event.id]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/events/${event.id}/reports/generate`);
      toast.success("Report generated");
      setActive(data);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setGenerating(false); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="font-display text-lg font-bold text-foreground">AI-assisted reports</h2>
          <p className="text-sm text-muted-foreground">Turn event activity into an executive report.</p>
        </div>
        <div className="flex items-center">
        <Button onClick={generate} disabled={generating} className="rounded-md bg-accent text-accent-foreground hover:opacity-90 transition-opacity" data-testid="generate-report-btn">
          {generating ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Generating…</> : <><Sparkles className="h-4 w-4 mr-1.5" /> Generate report</>}
        </Button>
        {active && (
          <Button variant="outline" onClick={() => generateReportPdf(event, active)} className="rounded-md ml-2" data-testid="download-pdf-btn">
            <Download className="h-4 w-4 mr-1.5" /> Download PDF
          </Button>
        )}
        </div>
      </div>

      {reports.length === 0 && !active ? (
        <EmptyState icon={FileBarChart} title="No reports yet"
          description="Generate an AI-assisted executive report from this event's questions, polls and feedback."
          action={<Button onClick={generate} disabled={generating} className="rounded-md">{generating ? "Generating…" : "Generate report"}</Button>} />
      ) : (
        <div className="grid lg:grid-cols-4 gap-4">
          <div className="lg:col-span-1 space-y-2" data-testid="reports-list">
            {reports.map((r) => (
              <button key={r.id} onClick={() => setActive(r)}
                className={`w-full text-left rounded-lg border p-3 transition-colors ${active?.id === r.id ? "border-accent bg-accent/5" : "border-border hover:bg-muted/40"}`}>
                <p className="text-sm font-medium text-foreground">Report</p>
                <p className="text-xs text-muted-foreground mt-0.5">{new Date(r.generated_at).toLocaleString()}</p>
              </button>
            ))}
          </div>
          <div className="lg:col-span-3">
            {active && <ReportView content={active.content} />}
          </div>
        </div>
      )}
    </div>
  );
}
